/**
 * Table Settings Menu Component
 *
 * A dropdown menu for table-level controls that provides:
 * - Column visibility toggles (show/hide columns)
 * - Per-column sort: header-style arrow cycle (unsorted → asc → desc → clear)
 * - Drag handles to reorder unfixed columns
 * - Select all / deselect all options
 *
 * Usage:
 * ```tsx
 * <TableSettingsMenu
 *   columns={table.getAllLeafColumns()}
 *   onColumnVisibilityChange={(columnId, visible) => {
 *     table.getColumn(columnId)?.toggleVisibility(visible);
 *   }}
 * />
 * ```
 */

"use client";

import type { ColumnSortDirection } from "@/lib/utils/column-order";
import { cn } from "@/lib/utils/general-utils";
import {
  closestCenter,
  DndContext,
  type CollisionDetection,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Columns3 as ViewColumnsIcon,
  GripVertical,
  Search,
  X,
} from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { ColumnSortControls } from "./column-sort-controls";

const { useMemo, useState, useRef, useEffect, useCallback } = React;

const DRAG_ACTIVATION_DISTANCE = 8;

export interface TableColumn {
  id: string;
  /** Column header label */
  label: string;
  /** Whether the column is currently visible */
  isVisible: boolean;
  /** Whether the column can be hidden (some columns like actions should always be visible) */
  canHide?: boolean;
  /** Whether the column can be reordered */
  canReorder?: boolean;
  /**
   * Show in the selector even when `canHide` is false (sticky/first column).
   * Actions and other utility columns omit this so they stay out of the menu.
   */
  alwaysVisible?: boolean;
  /** Whether the column can be sorted (table row order) */
  canSort?: boolean;
  /** Current sort direction for this column */
  sortDirection?: false | "asc" | "desc";
}

export interface TableSettingsMenuProps {
  /** Array of columns with visibility state */
  columns: TableColumn[];
  /** Handler for column visibility changes */
  onColumnVisibilityChange: (_columnId: string, _visible: boolean) => void;
  /** Optional button text (defaults to icon only) */
  buttonText?: string;
  /** Optional button className */
  buttonClassName?: string;
  /** Enable column reordering reset option */
  enableColumnReordering?: boolean;
  /** Handler for resetting column order */
  onResetColumnOrder?: () => void;
  /** Handler for reordering movable (unfixed) columns */
  onColumnOrderChange?: (_orderedIds: string[]) => void;
  /** Set this column's sort: asc, desc, or clear */
  onColumnSortChange?: (
    _columnId: string,
    _direction: ColumnSortDirection,
  ) => void;
}

function ColumnRow({
  column,
  showHandle,
  isDragging,
  style,
  setNodeRef,
  handleAttributes,
  handleListeners,
  setActivatorNodeRef,
  onVisibilityChange,
  onSortChange,
}: {
  column: TableColumn;
  showHandle: boolean;
  isDragging?: boolean;
  style?: React.CSSProperties;
  setNodeRef?: (_node: HTMLElement | null) => void;
  handleAttributes?: React.HTMLAttributes<HTMLElement>;
  handleListeners?: React.HTMLAttributes<HTMLElement>;
  setActivatorNodeRef?: (_node: HTMLElement | null) => void;
  onVisibilityChange: (_columnId: string, _visible: boolean) => void;
  onSortChange?: (_columnId: string, _direction: ColumnSortDirection) => void;
}) {
  const checkboxId = `column-visibility-${column.id}`;
  const locked = column.canHide === false;
  const showSort = Boolean(column.canSort && onSortChange);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "w-full min-w-0 text-sm transition-colors",
        column.isVisible && "bg-primary/5",
        isDragging && "opacity-70 shadow-sm z-10",
      )}
    >
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-2.5",
          !locked && "hover:bg-gray-50",
        )}
      >
        <input
          id={checkboxId}
          type="checkbox"
          checked={column.isVisible}
          disabled={locked}
          onChange={(e) => {
            if (locked) return;
            onVisibilityChange(column.id, e.target.checked);
          }}
          className="h-4 w-4 rounded border-gray-300 accent-primary shrink-0 disabled:cursor-not-allowed disabled:opacity-60"
          style={{
            accentColor: "var(--primary)",
          }}
        />
        <label
          htmlFor={checkboxId}
          className={cn(
            "font-medium flex-1 min-w-0",
            locked ? "cursor-default text-primary" : "cursor-pointer",
            !locked && (column.isVisible ? "text-primary" : "text-gray-700"),
          )}
        >
          {column.label}
        </label>
        {showSort && onSortChange ? (
          <div
            className="shrink-0"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <ColumnSortControls
              sortDirection={column.sortDirection ?? false}
              columnLabel={column.label}
              showLegend={false}
              onSortAsc={() => onSortChange(column.id, "asc")}
              onSortDesc={() => onSortChange(column.id, "desc")}
              onClearSort={() => onSortChange(column.id, false)}
            />
          </div>
        ) : null}
        {showHandle ? (
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing touch-none"
            aria-label={`Reorder ${column.label} column`}
            {...handleAttributes}
            {...handleListeners}
          >
            <GripVertical className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : (
          <span className="w-5 shrink-0" aria-hidden="true" />
        )}
      </div>
    </div>
  );
}

function SortableColumnRow({
  column,
  showHandle,
  onVisibilityChange,
  onSortChange,
}: {
  column: TableColumn;
  showHandle: boolean;
  onVisibilityChange: (_columnId: string, _visible: boolean) => void;
  onSortChange?: (_columnId: string, _direction: ColumnSortDirection) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    disabled: !showHandle,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, x: 0 } : null,
    ),
    transition,
  };

  return (
    <ColumnRow
      column={column}
      showHandle={showHandle}
      isDragging={isDragging}
      style={style}
      setNodeRef={setNodeRef}
      handleAttributes={attributes}
      handleListeners={listeners}
      setActivatorNodeRef={setActivatorNodeRef}
      onVisibilityChange={onVisibilityChange}
      onSortChange={onSortChange}
    />
  );
}

export function TableSettingsMenu({
  columns,
  onColumnVisibilityChange,
  buttonText,
  buttonClassName,
  enableColumnReordering = false,
  onResetColumnOrder,
  onColumnOrderChange,
  onColumnSortChange,
}: Readonly<TableSettingsMenuProps>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Hideable columns plus locked first/fixed columns (`alwaysVisible`)
  const listedColumns = useMemo(
    () =>
      columns.filter(
        (col) => col.canHide !== false || col.alwaysVisible === true,
      ),
    [columns],
  );

  // Count visible columns (always the full listed set, not the search filter)
  const visibleCount = useMemo(
    () => listedColumns.filter((col) => col.isVisible).length,
    [listedColumns],
  );

  const filteredColumns = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return listedColumns;
    return listedColumns.filter(
      (col) =>
        col.label.toLowerCase().includes(query) ||
        col.id.toLowerCase().includes(query),
    );
  }, [listedColumns, searchQuery]);

  const filteredHideable = useMemo(
    () => filteredColumns.filter((col) => col.canHide !== false),
    [filteredColumns],
  );

  const allVisible =
    filteredHideable.length === 0 ||
    filteredHideable.every((col) => col.isVisible);
  const noneVisible =
    filteredHideable.length === 0 ||
    filteredHideable.every((col) => !col.isVisible);

  const isSearching = searchQuery.trim().length > 0;
  const canDrag =
    enableColumnReordering && !!onColumnOrderChange && !isSearching;

  const sortableIds = useMemo(
    () =>
      listedColumns
        .filter((col) => col.canReorder !== false)
        .map((col) => col.id),
    [listedColumns],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = sortableIds.indexOf(String(active.id));
      const newIndex = sortableIds.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      onColumnOrderChange?.(arrayMove(sortableIds, oldIndex, newIndex));
    },
    [onColumnOrderChange, sortableIds],
  );

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const collisions = closestCenter(args);
      const allowed = new Set(sortableIds);
      return collisions.filter((collision) =>
        allowed.has(String(collision.id)),
      );
    },
    [sortableIds],
  );

  // Handle show all / hide all — scoped to the currently filtered columns
  const handleShowAll = () => {
    filteredColumns.forEach((col) => {
      if (!col.isVisible) {
        onColumnVisibilityChange(col.id, true);
      }
    });
  };

  const handleHideAll = () => {
    filteredHideable.forEach((col) => {
      if (col.isVisible) {
        onColumnVisibilityChange(col.id, false);
      }
    });
  };

  // Calculate menu position when it opens
  useEffect(() => {
    if (!menuOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: buttonRect.bottom + window.scrollY + 8, // 8px = mt-2 equivalent
        right: window.innerWidth - buttonRect.right + window.scrollX,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [menuOpen]);

  // Handle click outside to close menu
  useEffect(() => {
    if (!menuOpen) {
      setMenuPosition(null);
      setSearchQuery("");
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setMenuOpen(false);
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  // Toggle menu
  const handleToggleMenu = () => {
    setMenuOpen((prev) => {
      if (prev) {
        // Reset position when closing
        setMenuPosition(null);
      }
      return !prev;
    });
  };

  const columnList =
    filteredColumns.length === 0 ? (
      <p className="px-3 py-3 text-sm text-text-tertiary">No columns match</p>
    ) : canDrag ? (
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {filteredColumns.map((column) =>
            column.canReorder === false ? (
              <ColumnRow
                key={column.id}
                column={column}
                showHandle={false}
                onVisibilityChange={onColumnVisibilityChange}
                onSortChange={onColumnSortChange}
              />
            ) : (
              <SortableColumnRow
                key={column.id}
                column={column}
                showHandle
                onVisibilityChange={onColumnVisibilityChange}
                onSortChange={onColumnSortChange}
              />
            ),
          )}
        </SortableContext>
      </DndContext>
    ) : (
      filteredColumns.map((column) => (
        <ColumnRow
          key={column.id}
          column={column}
          showHandle={false}
          onVisibilityChange={onColumnVisibilityChange}
          onSortChange={onColumnSortChange}
        />
      ))
    );

  const dropdownContent = menuOpen && menuPosition && (
    <div
      ref={menuRef}
      className={cn(
        "fixed w-72 max-w-[min(18rem,calc(100vw-1rem))] overflow-x-hidden bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[300]",
        "flex flex-col",
      )}
      style={{
        top: `${menuPosition.top}px`,
        right: `${menuPosition.right}px`,
      }}
    >
      {/* Header */}
      <div className="px-3 py-2 text-xs font-semibold text-text-tertiary uppercase tracking-wider border-b border-gray-200">
        Column Visibility
      </div>

      {/* Search stays in view because only the checkbox list scrolls */}
      <div className="bg-white px-3 py-2 border-b border-gray-200">
        <div className="relative">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Escape") {
                e.preventDefault();
              }
            }}
            placeholder="Search columns…"
            className={cn(
              "block w-full h-8 pl-7 text-sm border rounded-md",
              searchQuery ? "pr-7" : "pr-2",
              "placeholder:text-gray-400 text-gray-900",
              "focus:outline-none focus:ring-1 focus:ring-gray-300 focus:border-gray-300",
              "[&::-webkit-search-cancel-button]:appearance-none",
              "border-gray-200 bg-white",
            )}
            aria-label="Search columns"
            autoComplete="off"
            autoFocus
          />
          {searchQuery ? (
            <button
              type="button"
              aria-label="Clear column search"
              onClick={() => setSearchQuery("")}
              className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Show/Hide All Actions */}
      <div className="px-3 py-2 border-b border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <button
            onClick={handleShowAll}
            disabled={allVisible}
            className={cn(
              "text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium",
            )}
            type="button"
          >
            Show all
          </button>
          <span>•</span>
          <button
            onClick={handleHideAll}
            disabled={noneVisible}
            className={cn(
              "text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium",
            )}
            type="button"
          >
            Hide all
          </button>
          <span className="ml-auto text-text-tertiary">
            {visibleCount} / {listedColumns.length}
          </span>
        </div>
      </div>

      {/* Column list with checkboxes */}
      <div className="max-h-96 overflow-y-auto overflow-x-hidden overscroll-contain">
        {columnList}
      </div>

      {/* Reset column order button */}
      {enableColumnReordering && onResetColumnOrder && (
        <>
          <div className="border-t border-gray-200 my-1" />
          <button
            onClick={() => {
              onResetColumnOrder();
              setMenuOpen(false);
            }}
            className={cn(
              "w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm transition-colors",
              "text-primary hover:bg-gray-50 font-medium",
            )}
            type="button"
          >
            <span>Reset Column Order</span>
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={handleToggleMenu}
          className={cn(
            "inline-flex items-center justify-center h-8 w-8 rounded-md border transition-all",
            "focus:outline-none",
            menuOpen
              ? "bg-primary/10 text-primary border-primary/30"
              : "bg-white text-text-tertiary border-gray-200 hover:bg-gray-50 hover:text-gray-700",
            buttonClassName,
          )}
          type="button"
          aria-expanded={menuOpen}
          aria-label="Table settings"
        >
          <ViewColumnsIcon className="h-4 w-4" aria-hidden="true" />
          {buttonText && <span>{buttonText}</span>}
        </button>
      </div>

      {/* Dropdown Menu - Rendered in Portal to avoid overflow clipping */}
      {typeof window !== "undefined" &&
        dropdownContent &&
        createPortal(dropdownContent, document.body)}
    </>
  );
}
